import React, { useMemo } from 'react';

import { formatInTimeZone } from 'date-fns-tz';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import CopyIcon from '@atlaskit/icon/core/migration/copy';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import CheckCircleIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { SimpleTag as Tag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import { G500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { isFedRamp } from '@atlassian/atl-context';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { CopyToClipboard } from '@atlassian/eoc-copy-to-clipboard';
import PriorityBadge from '@atlassian/eoc-priority-badge';
import { eocUserResource } from '@atlassian/eoc-resources';
import { ResponderName, useResponderNames } from '@atlassian/eoc-responder-names';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { MIN_DATE_SHOWN_EPOCH } from '../../../common/constants';
import { type Heartbeat, SupportUrlKeys } from '../../../common/types';
import { DeleteModal } from '../../../common/ui/delete-modal';
import { HeartbeatStatus } from '../../../common/ui/status';
import { SwitchModal } from '../../../common/ui/switch-modal';
import { getMaxAvailableEpoch } from '../../../common/utils/helpers';
import { useDeleteModalController } from '../../../controllers/delete-modal';
import { useHeartbeatsContext } from '../../../controllers/providers/heartbeats';
import { useSwitchController } from '../../../controllers/switch';
import { heartbeatsResource, heartbeatsUserRightsResource } from '../../../services/resources';

import messages from './messages';
import { Divider, StyledCode } from './styled';

const bottomBoxStyles = xcss({
	borderRadius: 'border.radius.200',
});

const topBoxStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: 'border.radius.200',
	borderWidth: 'border.width',
});

const attributeLabelStyles = xcss({
	width: '160px',
	flexShrink: 0,
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.semibold'),
	color: 'color.text.subtlest',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
});

const tagsLabelStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '28px',
});

const attributeValueStyles = xcss({
	minWidth: '0',
	wordBreak: 'break-word',
});

const contentWrapperStyles = xcss({
	maxWidth: '1024px',
	width: '100%',
});

const fullWidthStyles = xcss({
	width: '100%',
});

const badgeWrapperStyles = xcss({
	flexShrink: 0,
});

type Props = {
	data: Heartbeat;
};

export const Detail = ({ data: active }: Props) => {
	const { formatMessage } = useIntl();
	const [responder] = useResponderNames([{ type: 'team', id: active.ownerTeamId }]);
	const { data: user } = useResource(eocUserResource);
	const { data: response } = useResource(heartbeatsResource);
	const { data: rights } = useResource(heartbeatsUserRightsResource);

	assert(
		!!response && !!rights,
		new AssertionError('Detail', 'data coming from resources should not be undefined!'),
	);
	const timeZone = user?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

	const { focusState, isTransitioning, supportUrls } = useHeartbeatsContext();
	const { isDeleteModalOpen, handleDelete, handleDeleteClick, isDeleting, handleModalClose } =
		useDeleteModalController();
	const {
		handleSwitchClick,
		handleSwitch,
		isSwitchModalOpen,
		isSwitchLoading,
		handleModalClose: handleSwitchModalClose,
	} = useSwitchController();

	const data = useMemo(
		() => response.heartbeats.find((heartbeat) => heartbeat.name === active.name) ?? active,
		[active, response],
	);

	const apiPingUrl = supportUrls[SupportUrlKeys.API_PING_URL];
	const apiDocumentation = supportUrls[SupportUrlKeys.API_DOCUMENTATION];
	const createAPIToken = supportUrls[SupportUrlKeys.CREATE_API_TOKEN];
	const emailInstructions = supportUrls[SupportUrlKeys.EMAIL_INSTRUCTIONS];

	const apiUrl = useMemo(() => {
		const baseUrl = isFedRamp()
			? 'https://api.atlassian-us-gov-mod.com'
			: 'https://api.atlassian.com';
		return `${baseUrl}${apiPingUrl}?name=${data.name}`;
	}, [apiPingUrl, data.name]);

	const attributes = [
		{ label: formatMessage(messages.name), content: <Text as="p">{data.name}</Text> },
		{
			label: formatMessage(messages.description),
			content: <Text as="p">{data.description ?? '-'}</Text>,
		},
		{
			label: formatMessage(messages.assignedTeam),
			content: <ResponderName responder={responder} />,
		},
		{
			label: formatMessage(messages.lastPinged),
			content: (
				<Text as="p">
					{MIN_DATE_SHOWN_EPOCH <= data.lastReceivedAt &&
					data.lastReceivedAt <= getMaxAvailableEpoch()
						? formatInTimeZone(data.lastReceivedAt, timeZone, 'MMM d, y h:mm a')
						: '-'}
				</Text>
			),
		},
		{
			label: formatMessage(messages.nextPing),
			content: (
				<Text as="p">
					{MIN_DATE_SHOWN_EPOCH <= data.willExpireAt && data.willExpireAt <= getMaxAvailableEpoch()
						? formatInTimeZone(data.willExpireAt, timeZone, 'MMM d, y h:mm a')
						: '-'}
				</Text>
			),
		},
		{
			label: formatMessage(messages.pingInterval),
			content: (
				<Text as="p">
					{data.interval} {data.intervalUnit}
				</Text>
			),
		},
		{
			label: formatMessage(messages.status),
			content: <HeartbeatStatus heartbeat={data} />,
		},
	];

	return (
		<Flex justifyContent="center" xcss={contentWrapperStyles}>
			<Stack grow="fill" space="space.200">
				<Box paddingBlock="space.300">
					<Inline spread="space-between">
						<Heading size="large">{data.name}</Heading>
						<Inline space="space.100">
							{rights.hasEditRight && (
								<Button
									appearance="primary"
									onClick={() => {
										handleSwitchClick(data);
									}}
									isLoading={data.status === 'Off' && isSwitchLoading}
								>
									{data.status === 'Off' ? (
										<FormattedMessage {...messages.turOnButton} />
									) : (
										<FormattedMessage {...messages.turOffButton} />
									)}
								</Button>
							)}
							<Button appearance="subtle" onClick={focusState.close}>
								<FormattedMessage {...messages.close} />
							</Button>
							{rights.hasDeleteRight && (
								<DropdownMenu<HTMLButtonElement>
									trigger={({ triggerRef, ...props }) => (
										<IconButton
											label="more"
											{...props}
											icon={MoreIcon}
											ref={triggerRef}
											appearance="subtle"
										/>
									)}
									placement="bottom-end"
								>
									<DropdownItemGroup>
										<DropdownItem
											onClick={() => {
												handleDeleteClick(data);
											}}
										>
											<FormattedMessage {...messages.delete} />
										</DropdownItem>
									</DropdownItemGroup>
								</DropdownMenu>
							)}
						</Inline>
					</Inline>
				</Box>

				<Stack grow="fill" space="space.300">
					<Stack space="space.050">
						<Heading size="medium">
							<FormattedMessage {...messages.title} />
						</Heading>
					</Stack>
					{isTransitioning && fg('platform.show-optimal-tooling_brgom') && (
						<SectionMessage title={formatMessage(messages.transitioningTitle)}>
							<FormattedMessage {...messages.transitioningMessage} />
						</SectionMessage>
					)}
					<Box xcss={topBoxStyles} padding="space.300">
						<Inline xcss={fullWidthStyles} spread="space-between">
							<Stack xcss={fullWidthStyles} space="space.200">
								{attributes.map((attribute, index) => (
									<Inline alignBlock="start" key={index}>
										<Box xcss={attributeLabelStyles}>{attribute.label}</Box>
										<Box xcss={attributeValueStyles}>{attribute.content}</Box>
									</Inline>
								))}
							</Stack>
							{rights.hasEditRight && (
								<Button
									onClick={(_, analyticsEvent) => {
										fireUIAnalytics(analyticsEvent, 'editButton');

										focusState.open({ data: data, mode: 'edit' });
									}}
								>
									<FormattedMessage {...messages.edit} />
								</Button>
							)}
						</Inline>
						<Divider />
						<Stack space="space.200">
							{/* Alert message label */}
							<Inline alignBlock="start">
								<Box xcss={attributeLabelStyles}>{formatMessage(messages.alertMessage)}</Box>
								<Inline xcss={attributeValueStyles} alignBlock="center" space="space.100">
									<Box xcss={badgeWrapperStyles}>
										<PriorityBadge priority={data.alertPriority} />
									</Box>
									<Box>
										<Text as="p">{data.alertMessage}</Text>
									</Box>
								</Inline>
							</Inline>

							{/* Alert tags label */}
							<Inline alignBlock="start">
								<Box xcss={[attributeLabelStyles, tagsLabelStyles]}>
									{formatMessage(messages.alertTags)}
								</Box>
								<Inline xcss={attributeValueStyles} alignBlock="center" space="space.100">
									<TagGroup>
										{data.alertTags.map((alertTag) => (
											<Tooltip key={alertTag} position="bottom" content={alertTag}>
												<Tag text={alertTag} />
											</Tooltip>
										))}
									</TagGroup>
								</Inline>
							</Inline>
						</Stack>
					</Box>
					<Box
						backgroundColor="color.background.neutral.hovered"
						xcss={bottomBoxStyles}
						padding="space.300"
					>
						<Stack space="space.300">
							<Heading size="small">
								<FormattedMessage {...messages.instructions} />
							</Heading>

							{/* The line under tha tabs heading are not visible since it's the same with the background color of the box, talk with designer */}
							<Tabs id="default">
								<TabList>
									<Tab>
										<FormattedMessage {...messages.apiTabTitle} />
									</Tab>
									<Tab>
										<FormattedMessage {...messages.emailTabTitle} />
									</Tab>
								</TabList>
								<TabPanel>
									<Box paddingBlockStart="space.300">
										<Stack space="space.200">
											<Heading size="xxsmall" as="div">
												<FormattedMessage {...messages.apiInstruction} />
											</Heading>

											<Box paddingBlock="space.050">
												{/* TODO: there is margin-left on the ol element and it doesn't align with figma */}
												<Stack as="ol" space="space.200">
													<Box as="li">
														<Anchor href={createAPIToken} target="_blank" rel="noopener noreferrer">
															<FormattedMessage
																{...messages.apiInstructionSecondBulletCreateToken}
															/>
														</Anchor>
														<FormattedMessage {...messages.apiInstructionFirstBullet} />
													</Box>
													<Box as="li">
														<Stack space="space.100">
															<FormattedMessage
																{...messages.apiInstructionSecondBullet}
																tagName="span"
																values={{
																	linkApiDocumentation: (msg: React.ReactNode) => (
																		<Anchor
																			href={apiDocumentation}
																			target="_blank"
																			rel="noopener noreferrer"
																		>
																			{msg}
																		</Anchor>
																	),
																}}
															/>
															<Inline alignBlock="center">
																<StyledCode>{apiUrl}</StyledCode>
																<CopyToClipboard tooltip={messages.copy} value={apiUrl}>
																	{({ isCopied }) => (
																		<Text as="p">
																			{isCopied ? (
																				<CheckCircleIcon
																					spacing="spacious"
																					label=""
																					color={token('color.icon.success', G500)}
																				/>
																			) : (
																				<CopyIcon
																					color="currentColor"
																					spacing="spacious"
																					label=""
																				/>
																			)}
																		</Text>
																	)}
																</CopyToClipboard>
															</Inline>
														</Stack>
													</Box>
													<Box as="li">
														<FormattedMessage {...messages.apiInstructionThirdBullet} />
													</Box>
													<Box as="li">
														<FormattedMessage {...messages.apiInstructionFourthBullet} />
													</Box>
												</Stack>
											</Box>
										</Stack>
									</Box>
								</TabPanel>
								<TabPanel>
									<Box paddingBlockStart="space.300">
										<Stack space="space.200">
											<Heading size="xxsmall" as="div">
												<FormattedMessage {...messages.emailInstruction} />
											</Heading>

											<Box paddingBlock="space.050">
												{/* TODO: there is margin-left on the ol element and it doesn't align with figma */}
												<Stack as="ol" space="space.200">
													<Box as="li">
														<Stack space="space.100">
															<FormattedMessage {...messages.emailInstructionFirstBullet} />
															<Inline alignBlock="center">
																{/* TODO learn how to generate this URL */}
																<StyledCode>
																	{data.name}
																	{response.heartbeatEmailDomain}
																</StyledCode>
																<CopyToClipboard
																	tooltip={messages.copy}
																	value={`${data.name}${response.heartbeatEmailDomain}`}
																>
																	{({ isCopied }) => (
																		<Text as="p">
																			{isCopied ? (
																				<CheckCircleIcon
																					spacing="spacious"
																					label=""
																					color={token('color.icon.success', G500)}
																				/>
																			) : (
																				<CopyIcon
																					color="currentColor"
																					spacing="spacious"
																					label=""
																				/>
																			)}
																		</Text>
																	)}
																</CopyToClipboard>
															</Inline>
														</Stack>
													</Box>
													<Box as="li">
														<FormattedMessage {...messages.emailInstructionSecondBullet} />
													</Box>
												</Stack>
											</Box>

											<Anchor href={emailInstructions} target="_blank" rel="noopener noreferrer">
												<FormattedMessage {...messages.emailInstructionLearnMore} />
											</Anchor>
										</Stack>
									</Box>
								</TabPanel>
							</Tabs>
						</Stack>
					</Box>
				</Stack>
			</Stack>
			<DeleteModal
				onDeleteAttemptCancel={handleModalClose}
				onDelete={() => handleDelete().then(() => focusState.close())}
				isLoading={isDeleting}
				isDeleteDialogOpen={isDeleteModalOpen}
			/>
			<SwitchModal
				onSwitchAttemptCancel={handleSwitchModalClose}
				onSwitch={handleSwitch}
				isLoading={isSwitchLoading}
				isSwitchDialogOpen={isSwitchModalOpen}
			/>
		</Flex>
	);
};
